import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/dash/ui";

const TYPE_COLORS = {
  interview: "bg-indigo-100 text-indigo-700",
  rfq: "bg-blue-100 text-blue-700",
  contract: "bg-amber-100 text-amber-700",
  renewal: "bg-teal-100 text-teal-700",
  document: "bg-rose-100 text-rose-700",
  invoice: "bg-emerald-100 text-emerald-700",
};
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Calendar() {
  const nav = useNavigate();
  const [events, setEvents] = React.useState([]);
  const [cursor, setCursor] = React.useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  React.useEffect(() => { api.get("/calendar").then((r) => setEvents(r.data.events || [])).catch(() => setEvents([])); }, []);

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const byDate = events.reduce((a, e) => { (a[e.date] = a[e.date] || []).push(e); return a; }, {});
  const monthLabel = cursor.toLocaleString("en-US", { month: "long", year: "numeric" });

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const key = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const today = new Date();
  const isToday = (d) => today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  return (
    <div>
      <PageHeader title="Business Calendar" description="Interviews, RFQ deadlines, contract & document expiries and invoice due dates." breadcrumb="Workspace / Calendar">
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="h-9 w-9 rounded-lg border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
          <span className="font-display font-semibold text-slate-800 w-40 text-center">{monthLabel}</span>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="h-9 w-9 rounded-lg border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </PageHeader>
      <Card>
        <div className="grid grid-cols-7 gap-px mb-2">
          {DOW.map((d) => <div key={d} className="text-center text-xs font-semibold text-slate-400 uppercase tracking-wide py-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((d, i) => (
            <div key={i} className={`min-h-[96px] rounded-lg border p-1.5 ${d ? "border-slate-100 bg-white" : "border-transparent"} ${d && isToday(d) ? "ring-1 ring-vs-royal" : ""}`}>
              {d && <div className={`text-xs font-semibold mb-1 ${isToday(d) ? "text-vs-royal" : "text-slate-500"}`}>{d}</div>}
              <div className="space-y-1">
                {d && (byDate[key(d)] || []).slice(0, 3).map((e, j) => (
                  <button key={j} onClick={() => nav(`/app${e.link}`)} data-testid="cal-event"
                    className={`w-full text-left truncate rounded px-1.5 py-0.5 text-[10px] font-medium ${TYPE_COLORS[e.type] || "bg-slate-100 text-slate-600"}`} title={e.title}>{e.title}</button>
                ))}
                {d && (byDate[key(d)] || []).length > 3 && <div className="text-[10px] text-slate-400 px-1.5">+{byDate[key(d)].length - 3} more</div>}
              </div>
            </div>
          ))}
        </div>
        {events.length === 0 && <div className="mt-6"><EmptyState icon="CalendarDays" title="No scheduled events yet" description="Interviews, deadlines and due dates will appear here as your business activity grows." /></div>}
      </Card>
    </div>
  );
}
